/**
 * ScheduledReportsDialog - Dialog for configuring scheduled spending reports
 */
import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { 
  Calendar, 
  Mail,
  Loader2,
  Check,
  FileText,
  TrendingUp,
  Lightbulb,
  PieChart,
  Send,
  History
} from 'lucide-react';
import { getReportPreferences, updateReportPreferences, sendReportNow, getReportHistory } from '../../lib/api';
import { toast } from 'sonner';

const DAYS_OF_WEEK = [
  { value: '0', label: 'Monday' },
  { value: '1', label: 'Tuesday' },
  { value: '2', label: 'Wednesday' },
  { value: '3', label: 'Thursday' },
  { value: '4', label: 'Friday' },
  { value: '5', label: 'Saturday' },
  { value: '6', label: 'Sunday' },
];

const DAYS_OF_MONTH = Array.from({ length: 28 }, (_, i) => ({
  value: String(i + 1),
  label: `${i + 1}${['st', 'nd', 'rd'][i] || 'th'}`
}));

const ScheduledReportsDialog = ({ trigger }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [prefs, setPrefs] = useState({
    weekly_report_enabled: false,
    monthly_report_enabled: true,
    report_day_weekly: 0,
    report_day_monthly: 1,
    include_category_breakdown: true,
    include_spending_trends: true,
    include_savings_tips: true,
    email: ''
  });
  const [reportHistory, setReportHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (open) {
      fetchPreferences();
      fetchHistory();
    }
  }, [open]);

  const fetchPreferences = async () => {
    setLoading(true);
    try {
      const response = await getReportPreferences();
      setPrefs(response.data);
    } catch (error) {
      // Use defaults
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await getReportHistory(5);
      setReportHistory(response.data.reports || []);
    } catch (error) {
      // Ignore
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateReportPreferences(prefs);
      toast.success('Report preferences saved');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleSendNow = async (type) => {
    setSending(true);
    try {
      const response = await sendReportNow(type);
      if (response.data.sent) {
        toast.success(`${type === 'weekly' ? 'Weekly' : 'Monthly'} report sent to ${response.data.email}`);
        fetchHistory();
      } else {
        toast.info(response.data.message || 'Report generated (email not configured)');
      }
    } catch (error) {
      toast.error('Failed to send report');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" className="rounded-full" data-testid="scheduled-reports-btn">
            <FileText className="w-4 h-4 mr-2" />
            Scheduled Reports
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Calendar className="w-5 h-5 text-emerald-500" /> Scheduled Spending Reports
          </DialogTitle>
          <DialogDescription>
            Receive automatic spending summaries with insights and savings tips
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
          </div>
        ) : (
          <div className="space-y-6 py-4">
            {/* Weekly Report Toggle */}
            <div className="p-4 rounded-xl border">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-blue-600 border-blue-200">Weekly</Badge>
                  <span className="font-medium">Weekly Report</span>
                </div>
                <Switch
                  checked={prefs.weekly_report_enabled}
                  onCheckedChange={(v) => setPrefs(p => ({ ...p, weekly_report_enabled: v }))}
                  data-testid="weekly-report-toggle"
                />
              </div>
              {prefs.weekly_report_enabled && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Send every</span>
                  <Select
                    value={String(prefs.report_day_weekly)}
                    onValueChange={(v) => setPrefs(p => ({ ...p, report_day_weekly: parseInt(v) }))}
                  >
                    <SelectTrigger className="w-32 h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DAYS_OF_WEEK.map(day => (
                        <SelectItem key={day.value} value={day.value}>{day.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Monthly Report Toggle */}
            <div className="p-4 rounded-xl border">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-emerald-600 border-emerald-200">Monthly</Badge>
                  <span className="font-medium">Monthly Report</span>
                </div>
                <Switch
                  checked={prefs.monthly_report_enabled}
                  onCheckedChange={(v) => setPrefs(p => ({ ...p, monthly_report_enabled: v }))}
                  data-testid="monthly-report-toggle"
                />
              </div>
              {prefs.monthly_report_enabled && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Send on the</span>
                  <Select
                    value={String(prefs.report_day_monthly)}
                    onValueChange={(v) => setPrefs(p => ({ ...p, report_day_monthly: parseInt(v) }))}
                  >
                    <SelectTrigger className="w-24 h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DAYS_OF_MONTH.map(day => (
                        <SelectItem key={day.value} value={day.value}>{day.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="text-muted-foreground">of each month</span>
                </div>
              )}
            </div>

            {/* Report Content Options */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Include in Reports</Label>
              <div className="grid grid-cols-1 gap-2">
                <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors">
                  <Switch
                    checked={prefs.include_category_breakdown}
                    onCheckedChange={(v) => setPrefs(p => ({ ...p, include_category_breakdown: v }))}
                  />
                  <PieChart className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm">Category Breakdown</span>
                </label>
                <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors">
                  <Switch
                    checked={prefs.include_spending_trends}
                    onCheckedChange={(v) => setPrefs(p => ({ ...p, include_spending_trends: v }))}
                  />
                  <TrendingUp className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm">Spending Trends</span>
                </label>
                <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors">
                  <Switch
                    checked={prefs.include_savings_tips}
                    onCheckedChange={(v) => setPrefs(p => ({ ...p, include_savings_tips: v }))}
                  />
                  <Lightbulb className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm">Personalized Savings Tips</span>
                </label>
              </div>
            </div>

            {/* Report History */}
            {reportHistory.length > 0 && (
              <div>
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <History className="w-4 h-4" />
                  {showHistory ? 'Hide' : 'Show'} Recent Reports ({reportHistory.length})
                </button>
                {showHistory && (
                  <div className="mt-2 space-y-2">
                    {reportHistory.map((report) => (
                      <div key={`${report.report_type}-${report.sent_at}`} className="flex items-center justify-between p-2 rounded-lg bg-muted/50 text-sm">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {report.report_type}
                          </Badge>
                          <span className="text-muted-foreground">
                            {new Date(report.sent_at).toLocaleDateString()}
                          </span>
                        </div>
                        <span className="font-medium">${report.total_spent?.toFixed(2) || '0.00'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col gap-3 pt-2">
              <Button onClick={handleSave} disabled={saving} className="rounded-xl" data-testid="save-report-prefs">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Save Preferences
              </Button>
              
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => handleSendNow('weekly')}
                  disabled={sending}
                  className="flex-1 rounded-xl"
                  data-testid="send-weekly-now"
                >
                  {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Send Weekly Now
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleSendNow('monthly')}
                  disabled={sending}
                  className="flex-1 rounded-xl"
                  data-testid="send-monthly-now"
                >
                  {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Send Monthly Now
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ScheduledReportsDialog;
